/**
 * R160h · Feb 2026 — Daily Task Completion Report page.
 *
 * Route: /app/reports/daily-completion
 *
 * KPIs → charts → filterable/groupable/sortable task list, all sharing the
 * same server-side filters so every number the user sees agrees. Backed
 * by /workspaces/{ws}/reports/daily-completion.
 */
import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import {
  RefreshCw, Download, Send, Settings2, CalendarDays, Filter, Search, ArrowUpDown, ArrowUp, ArrowDown, X, ChevronDown, ChevronRight,
  CheckCircle2, CircleDashed, Share2,
} from "lucide-react";
import DailyReportSettingsDialog from "@/components/reports/DailyReportSettingsDialog";
import MultiSelect from "@/components/ui/multi-select";
import UserTimesheetHover from "@/components/reports/UserTimesheetHover";
import { TaskDetail } from "@/pages/app/Tasks";
import PriorityIcon from "@/components/tasks/PriorityIcon";

const TIMING_LABEL = { ahead: "Ahead", on_time: "On time", delayed: "Delayed", no_due: "No due" };
const TIMING_COLOR = { ahead: "#059669", on_time: "#2563eb", delayed: "#dc2626", no_due: "#94a3b8" };
const EFFORT_LABEL = { under: "Under effort", on: "On effort", over: "Over effort", no_effort: "No effort" };
const EFFORT_COLOR = { under: "#0891b2", on: "#059669", over: "#d97706", no_effort: "#94a3b8" };

// R268~10 · Feb 2026 — Hoisted so MobileCard (module-scope) can reuse.
const hoursFmt = (v) => (v == null ? "—" : `${(+v).toFixed(1)}h`);
// R268~25 · Feb 2026 — Human-readable date formatter for due /
// completed columns. Renders "Aug 18, 2026" so managers don't have to
// mentally parse ISO 8601. Falls back to the em-dash when null.
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtNiceDate = (v) => {
  if (!v) return "—";
  const iso = String(v).slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${MONTHS[(+m) - 1] || m} ${(+d)}, ${y}`;
};
// Days between a due date and either a completion date or today. Null
// when there's no due date. Positive = late, negative/zero = on time.
const daysDelayed = (dueDate, completedAt) => {
  if (!dueDate) return null;
  const due = new Date(String(dueDate).slice(0, 10) + "T00:00:00Z");
  const end = completedAt
    ? new Date(String(completedAt).slice(0, 10) + "T00:00:00Z")
    : new Date(new Date().toISOString().slice(0, 10) + "T00:00:00Z");
  const diff = Math.round((end - due) / 86400000);
  return diff;
};
const varFmt = (t) => {
  if (t.effort_variance_pct == null) return "—";
  const v = t.effort_variance_pct;
  const tone = v > 10 ? "text-orange-600" : v < -10 ? "text-cyan-600" : "text-emerald-600";
  const sign = v > 0 ? "+" : "";
  return <span className={tone}>{sign}{v.toFixed(1)}%</span>;
};
const PRIORITY_COLOR = { urgent: "#dc2626", high: "#ea580c", medium: "#2563eb", low: "#059669" };

const AXIOS_BASE = api.defaults?.baseURL || "";

export default function ReportsDailyCompletion() {
  const { activeWorkspaceId } = useAuth();
  const today = format(new Date(), "yyyy-MM-dd");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [assigneeFilter, setAssigneeFilter] = useState(new Set());
  const [projectFilter, setProjectFilter] = useState(new Set());
  const [priorityFilter, setPriorityFilter] = useState(new Set());
  const [timingFilter, setTimingFilter] = useState(new Set());
  const [effortFilter, setEffortFilter] = useState(new Set());
  const [clientFilter, setClientFilter] = useState(new Set());
  const [brandFilter, setBrandFilter] = useState(new Set());
  const [pmFilter, setPmFilter] = useState(new Set());
  // R268~15 · Feb 2026 — Department filter chip so senior managers
  // can slice the report to just Design / Ops / Content / etc.
  const [deptFilter, setDeptFilter] = useState(new Set());
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    api.get(`/workspaces/${activeWorkspaceId}/departments`)
      .then(({ data }) => { if (!cancelled) setDepartments(Array.isArray(data) ? data : (data?.rows || [])); })
      .catch(() => { if (!cancelled) setDepartments([]); });
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);
  // Feb 2026 — Task detail popup + one-table grouping controls.
  const [openTask, setOpenTask] = useState(null);   // full task obj for TaskDetail dialog
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // R268gggg · Feb 2026 — Secondary grouping inside each COMPLETED /
  // IN PROGRESS section. Default = project (most-requested view: "which
  // projects did each person deliver on today"). `collapsedSubs` keys
  // are `${parentKey}::${kind}` for whole status section OR
  // `${parentKey}::${kind}::${subKey}` for a single sub-group.
  const [subGroupBy, setSubGroupBy] = useState("project_name");
  // R268~10 · Feb 2026 — Mobile-only layout preference. "table" keeps
  // the compact table on phones; "cards" renders a stacked card list
  // (task title + status/priority icons, Est/Actual/Δ% pill row, notes
  // underneath). The toggle sits next to Expand/Collapse and is
  // hidden ≥ sm since the table is already the natural layout there.
  const [mobileView, setMobileView] = useState(() => {
    try { return localStorage.getItem("dcr:mobileView") || "table"; }
    catch { return "table"; }
  });
  useEffect(() => {
    try { localStorage.setItem("dcr:mobileView", mobileView); }
    catch { /* ignore */ }
  }, [mobileView]);
  // R268~11 · Feb 2026 — Export-busy flag for the html2canvas snapshot.
  const [exportBusy, setExportBusy] = useState(false);
  const [collapsedSubs, setCollapsedSubs] = useState(new Set());

  const openTaskById = async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/tasks/${tid}`);
      setOpenTask(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't open task")); }
  };
  const patchOpenTask = async (patch) => {
    if (!openTask) return;
    try {
      await api.patch(`/tasks/${openTask.id}`, patch);
      setOpenTask((d) => d ? { ...d, ...patch } : d);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("assignee_name");
  const [sortBy, setSortBy] = useState("completed_at");
  const [sortDir, setSortDir] = useState("desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  // Filter option catalogues — fetched once per workspace and reused by
  // every MultiSelect so the dropdowns stay in sync even when the report
  // itself has zero matching rows (e.g. very narrow date range).
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const [m, p, c, b] = await Promise.all([
          api.get(`/workspaces/${activeWorkspaceId}/members`),
          api.get(`/workspaces/${activeWorkspaceId}/projects`),
          api.get(`/workspaces/${activeWorkspaceId}/clients`),
          api.get(`/workspaces/${activeWorkspaceId}/brands`),
        ]);
        setMembers(m.data?.rows || m.data || []);
        setProjects(p.data?.rows || p.data || []);
        setClients(c.data?.rows || c.data || []);
        setBrands(b.data?.rows || b.data || []);
      } catch { /* keep last known options */ }
    })();
  }, [activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
      if (assigneeFilter.size) qs.set("assignee_ids", [...assigneeFilter].join(","));
      if (projectFilter.size) qs.set("project_ids", [...projectFilter].join(","));
      if (priorityFilter.size) qs.set("priorities", [...priorityFilter].join(","));
      if (timingFilter.size) qs.set("timings", [...timingFilter].join(","));
      if (effortFilter.size) qs.set("effort_statuses", [...effortFilter].join(","));
      if (clientFilter.size) qs.set("client_ids", [...clientFilter].join(","));
      if (brandFilter.size) qs.set("brand_ids", [...brandFilter].join(","));
      if (pmFilter.size) qs.set("pm_ids", [...pmFilter].join(","));
      if (deptFilter.size) qs.set("department_ids", [...deptFilter].join(","));
      if (search.trim()) qs.set("search", search.trim());
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/reports/daily-completion?${qs.toString()}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load report")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeWorkspaceId, dateFrom, dateTo, assigneeFilter, projectFilter, priorityFilter, timingFilter, effortFilter, clientFilter, brandFilter, pmFilter, deptFilter]);
  // Search runs on submit (Enter or button click) so we don't hammer the API.
  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const downloadPdf = async () => {
    try {
      const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
      if (assigneeFilter.size) qs.set("assignee_ids", [...assigneeFilter].join(","));
      if (projectFilter.size) qs.set("project_ids", [...projectFilter].join(","));
      if (priorityFilter.size) qs.set("priorities", [...priorityFilter].join(","));
      if (timingFilter.size) qs.set("timings", [...timingFilter].join(","));
      if (effortFilter.size) qs.set("effort_statuses", [...effortFilter].join(","));
      if (clientFilter.size) qs.set("client_ids", [...clientFilter].join(","));
      if (brandFilter.size) qs.set("brand_ids", [...brandFilter].join(","));
      if (pmFilter.size) qs.set("pm_ids", [...pmFilter].join(","));
      if (deptFilter.size) qs.set("department_ids", [...deptFilter].join(","));
      if (search.trim()) qs.set("search", search.trim());
      const resp = await api.get(`/workspaces/${activeWorkspaceId}/reports/daily-completion/pdf?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-completion-${dateFrom}${dateTo !== dateFrom ? `_to_${dateTo}` : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "PDF download failed")); }
  };

  // R268aaaa · Feb 2026 — Excel export with the same server-side
  // filters as the on-screen table + PDF export.
  const downloadXlsx = async () => {
    try {
      const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
      if (assigneeFilter.size) qs.set("assignee_ids", [...assigneeFilter].join(","));
      if (projectFilter.size) qs.set("project_ids", [...projectFilter].join(","));
      if (priorityFilter.size) qs.set("priorities", [...priorityFilter].join(","));
      if (timingFilter.size) qs.set("timings", [...timingFilter].join(","));
      if (effortFilter.size) qs.set("effort_statuses", [...effortFilter].join(","));
      if (clientFilter.size) qs.set("client_ids", [...clientFilter].join(","));
      if (brandFilter.size) qs.set("brand_ids", [...brandFilter].join(","));
      if (pmFilter.size) qs.set("pm_ids", [...pmFilter].join(","));
      if (deptFilter.size) qs.set("department_ids", [...deptFilter].join(","));
      if (search.trim()) qs.set("search", search.trim());
      const resp = await api.get(`/workspaces/${activeWorkspaceId}/reports/daily-completion/xlsx?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-completion-${dateFrom}${dateTo !== dateFrom ? `_to_${dateTo}` : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Excel download failed")); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/reports/daily-completion/send-now?date=${dateFrom}`);
      if (data.ok) toast.success(`Sent to ${data.recipients.length} recipient${data.recipients.length===1?"":"s"} · ${data.task_count} tasks`);
      else toast.error("Send failed — check workspace mailbox is configured");
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSendingNow(false); }
  };

  const toggle = (setState) => (key) => setState((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearFilters = () => {
    setAssigneeFilter(new Set()); setProjectFilter(new Set());
    setPriorityFilter(new Set()); setTimingFilter(new Set());
    setEffortFilter(new Set()); setClientFilter(new Set());
    setBrandFilter(new Set()); setPmFilter(new Set()); setDeptFilter(new Set()); setSearch("");
  };

  // Sort + group applied client-side for zero-latency table interactions.
  // R265 · Feb 2026 — Merge completed + in-progress-today tasks so each
  // person's full daily contribution is visible in one list. `kind` on
  // each row lets the grouping build the sub-nodes.
  const sortedTasks = useMemo(() => {
    if (!data?.tasks) return [];
    const merged = [
      ...(data.tasks || []).map((t) => ({ ...t, kind: t.kind || "completed" })),
      ...(data.progress_tasks || []).map((t) => ({ ...t, kind: "progress" })),
    ];
    const dir = sortDir === "asc" ? 1 : -1;
    merged.sort((a, b) => {
      const va = a[sortBy] ?? "";
      const vb = b[sortBy] ?? "";
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return merged;
  }, [data?.tasks, data?.progress_tasks, sortBy, sortDir]);
  // R268~27 · Feb 2026 — Client-side "quick filter" scoped strictly to
  // the already-loaded rows. Complements the top server-side search
  // (which triggers a network round-trip): as the user types, matching
  // rows survive and their parent group / date / status headers stay
  // visible so the hierarchy never breaks. Matches against every
  // human-readable field so a manager can type "hero", "video_proj",
  // "aug 18", or an assignee name and everything lines up.
  const [quickFilter, setQuickFilter] = useState("");
  const filteredTasks = useMemo(() => {
    const q = quickFilter.trim().toLowerCase();
    if (!q) return sortedTasks;
    const hit = (v) => v != null && String(v).toLowerCase().includes(q);
    return sortedTasks.filter((t) => (
      hit(t.title) ||
      hit(t.assignee_name) ||
      hit(t.project_name) ||
      hit(t.client_name) ||
      hit(t.brand_name) ||
      hit(t.department_name) ||
      hit(t.priority) ||
      hit(t.status_label) ||
      hit(t.due_date) || hit(fmtNiceDate(t.due_date)) ||
      hit(t.completed_at) || hit(fmtNiceDate(t.completed_at)) ||
      (Array.isArray(t.notes) && t.notes.some((n) => hit(n.note)))
    ));
  }, [sortedTasks, quickFilter]);
  const groups = useMemo(() => {
    // R268~19 · Feb 2026 — If primary is None but secondary is set, wrap
    // all tasks in a single synthetic parent so `Then by` still renders
    // its status → sub-group hierarchy.
    if (!groupBy || groupBy === "none") {
      if (subGroupBy && subGroupBy !== "none") {
        return [["All tasks", filteredTasks]];
      }
      return null;
    }
    const m = new Map();
    for (const t of filteredTasks) {
      const key = t[groupBy] || (groupBy.endsWith("_name") ? "—" : (groupBy === "priority" ? "medium" : ""));
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [filteredTasks, groupBy, subGroupBy]);

  // R268zzz · Feb 2026 — Set of every assignee_id currently visible in
  // the main data table for the selected range. Passed to
  // `SilentMembersSection` so users with any completed OR in-progress
  // task in the window are removed from the "silent" list.
  const visibleAssigneeIds = useMemo(() => {
    const s = new Set();
    for (const t of sortedTasks) { if (t.assignee_id) s.add(t.assignee_id); }
    return s;
  }, [sortedTasks]);

  // R268~20 · Feb 2026 — Data for the "Hours by <Group by>" chart that
  // sits above the table. Sums grand-total actual_hours per group,
  // splitting Done vs In progress so managers can eyeball who / what /
  // where the day's effort landed. Skipped when Group by=None (nothing
  // meaningful to chart) or when the synthetic 'All tasks' group is in
  // use. Cap at top-15 by total for readability; remainder rolled into
  // an "Other" bar so the totals still reconcile.
  const groupHoursChart = useMemo(() => {
    if (!groupBy || groupBy === "none") return null;
    if (!groups || groups.length === 0) return null;
    if (groups.length === 1 && groups[0][0] === "All tasks") return null;
    const rows = groups.map(([label, tasks]) => {
      let done = 0, prog = 0;
      for (const t of tasks) {
        const h = Number(t.actual_hours) || 0;
        if (t.kind === "progress") prog += h; else done += h;
      }
      return { name: label || "—", done, progress: prog, total: done + prog };
    }).sort((a, b) => b.total - a.total);
    if (rows.length <= 15) return rows;
    const top = rows.slice(0, 14);
    const rest = rows.slice(14).reduce((acc, r) => {
      acc.done += r.done; acc.progress += r.progress; acc.total += r.total;
      return acc;
    }, { name: `Other (${rows.length - 14})`, done: 0, progress: 0, total: 0 });
    return [...top, rest];
  }, [groups, groupBy]);

  const groupByLabel = {
    assignee_name: "Assignee",
    project_name: "Project",
    client_name: "Client",
    brand_name: "Brand",
    department_name: "Department",
    status_label: "Status",
    priority: "Priority",
    timing: "Timing",
    effort_status: "Effort",
  }[groupBy] || "Group";

  const t = data?.totals || {};

  return (
    <div className="space-y-6 px-4 sm:px-6 md:px-8 py-6" data-testid="daily-completion-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>Daily Completion Report</h1>
          <p className="text-sm text-muted-foreground mt-1">Who finished what, ahead of time or over budget — for a day or a range.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 border border-border rounded-md px-2 py-1 bg-background">
            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-transparent text-sm outline-none w-32" data-testid="report-date-from" />
            <span className="text-muted-foreground text-xs">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-transparent text-sm outline-none w-32" data-testid="report-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="report-download-pdf">
            <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={downloadXlsx} data-testid="report-download-xlsx">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={sendNow} disabled={sendingNow} data-testid="report-send-now">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {sendingNow ? "Sending…" : "Send email"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="report-settings">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Settings
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-kpis">
        <Kpi label="Completed" value={t.completed} testid="kpi-completed" />
        <Kpi label="Ahead" value={t.ahead} tone="text-emerald-600" testid="kpi-ahead" />
        <Kpi label="On time" value={t.on_time} tone="text-blue-600" testid="kpi-on-time" />
        <Kpi label="Delayed" value={t.delayed} tone="text-red-600" testid="kpi-delayed" />
        <Kpi label="Under effort" value={t.under_effort} tone="text-cyan-600" testid="kpi-under-effort" />
        <Kpi label="On effort" value={t.on_effort} tone="text-emerald-600" testid="kpi-on-effort" />
        <Kpi label="Over effort" value={t.over_effort} tone="text-orange-600" testid="kpi-over-effort" />
        <Kpi label="No effort" value={t.no_effort} tone="text-slate-500" testid="kpi-no-effort" />
      </div>

      {/* Multi-select filter row — sorted, searchable dropdowns for every
          key facet. Charts remain click-to-filter; these give explicit
          control (esp. for values the top-10 chart hides). */}
      <div className="flex flex-wrap items-center gap-2 border border-border/60 bg-muted/20 px-3 py-2 rounded-md"
           data-testid="filter-row">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        <MultiSelect
          testid="filter-clients"
          allLabel="All clients"
          width="w-44"
          options={clients.map((c) => ({ value: c.id, label: c.name || "—" }))}
          value={[...clientFilter]}
          onChange={(v) => setClientFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-brands"
          allLabel="All brands"
          width="w-44"
          options={brands.map((b) => ({ value: b.id, label: b.name || "—" }))}
          value={[...brandFilter]}
          onChange={(v) => setBrandFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-projects"
          allLabel="All projects"
          width="w-48"
          options={projects.map((p) => ({ value: p.id, label: p.name || "—" }))}
          value={[...projectFilter]}
          onChange={(v) => setProjectFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-assignees"
          allLabel="All assignees"
          width="w-44"
          options={members.map((m) => ({
            value: m.user_id || m.id,
            label: m.name || m.user_name || m.email || (m.user_id || m.id),
          }))}
          value={[...assigneeFilter]}
          onChange={(v) => setAssigneeFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-project-managers"
          allLabel="All PMs"
          width="w-44"
          options={[...members].sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "")).map((m) => ({
            value: m.user_id || m.id,
            label: m.name || m.user_name || m.email || (m.user_id || m.id),
          }))}
          value={[...pmFilter]}
          onChange={(v) => setPmFilter(new Set(v))}
        />
        {/* R268~15 · Feb 2026 — Department filter for senior manager
            view. Reads from `/workspaces/{ws}/departments` and
            forwards `department_ids=` to the two DCR endpoints. */}
        <MultiSelect
          testid="filter-department"
          allLabel="All departments"
          width="w-44"
          options={[...departments]
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((d) => ({ value: d.id, label: d.name }))}
          value={[...deptFilter]}
          onChange={(v) => setDeptFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-priority"
          allLabel="All priorities"
          width="w-40"
          options={["urgent", "high", "medium", "low"].map((p) => ({ value: p, label: p }))}
          value={[...priorityFilter]}
          onChange={(v) => setPriorityFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-timing"
          allLabel="All timings"
          width="w-40"
          options={["ahead", "on_time", "delayed", "no_due"].map((k) => ({ value: k, label: TIMING_LABEL[k] || k }))}
          value={[...timingFilter]}
          onChange={(v) => setTimingFilter(new Set(v))}
        />
        <MultiSelect
          testid="filter-effort"
          allLabel="All effort"
          width="w-40"
          options={["under", "on", "over", "no_effort"].map((k) => ({ value: k, label: EFFORT_LABEL[k] || k }))}
          value={[...effortFilter]}
          onChange={(v) => setEffortFilter(new Set(v))}
        />
        {(assigneeFilter.size + projectFilter.size + priorityFilter.size + timingFilter.size + effortFilter.size + clientFilter.size + brandFilter.size + pmFilter.size + deptFilter.size) > 0 && (
          <button type="button"
                  className="ml-auto text-xs underline text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                  data-testid="filter-row-clear">
            Clear all filters
          </button>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="By assignee" testid="chart-assignee">
          {/* Feb 2026 — Height scales with bar count + Y-axis width bumped
              so 20-char names don't get truncated to unreadable ellipses. */}
          <ResponsiveContainer width="100%" height={Math.max(260, (data?.by_assignee || []).slice(0, 10).length * 26 + 40)}>
            <BarChart data={(data?.by_assignee || []).slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" width={160}
                     tick={{ fontSize: 11 }} interval={0}
                     tickFormatter={(v) => (v && v.length > 22 ? v.slice(0, 21) + "…" : v)} />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}
                    onClick={(o) => o?.key && toggle(setAssigneeFilter)(o.key)}
                    style={{ cursor: "pointer" }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By project" testid="chart-project">
          <ResponsiveContainer width="100%" height={Math.max(260, (data?.by_project || []).slice(0, 10).length * 26 + 40)}>
            <BarChart data={(data?.by_project || []).slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" width={180}
                     tick={{ fontSize: 11 }} interval={0}
                     tickFormatter={(v) => (v && v.length > 26 ? v.slice(0, 25) + "…" : v)} />
              <Tooltip />
              <Bar dataKey="count" fill="#0891b2" radius={[0, 4, 4, 0]}
                    onClick={(o) => o?.key && toggle(setProjectFilter)(o.key)}
                    style={{ cursor: "pointer" }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By priority" testid="chart-priority">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data?.by_priority || []} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={80} label>
                {(data?.by_priority || []).map((entry) => (
                  <Cell key={entry.key} fill={PRIORITY_COLOR[entry.key] || "#94a3b8"} onClick={() => toggle(setPriorityFilter)(entry.key)} style={{ cursor: "pointer" }} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Timing" testid="chart-timing">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(data?.by_timing || []).map(x => ({...x, label: TIMING_LABEL[x.key] || x.key}))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}
                    onClick={(o) => o?.key && toggle(setTimingFilter)(o.key)}
                    style={{ cursor: "pointer" }}>
                {(data?.by_timing || []).map((entry) => (
                  <Cell key={entry.key} fill={TIMING_COLOR[entry.key] || "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Effort breakdown */}
      <ChartCard title="Effort" testid="chart-effort">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={(data?.by_effort || []).map(x => ({...x, label: EFFORT_LABEL[x.key] || x.key}))} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}
                  onClick={(o) => o?.key && toggle(setEffortFilter)(o.key)}
                  style={{ cursor: "pointer" }}>
              {(data?.by_effort || []).map((entry) => (
                <Cell key={entry.key} fill={EFFORT_COLOR[entry.key] || "#94a3b8"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Active filter chips */}
      {(assigneeFilter.size + projectFilter.size + priorityFilter.size + timingFilter.size + effortFilter.size + clientFilter.size + brandFilter.size + pmFilter.size) > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          {[...clientFilter].map(id => <FilterChip key={"c"+id} label={clients.find(x=>x.id===id)?.name || id} onClear={() => toggle(setClientFilter)(id)} />)}
          {[...brandFilter].map(id => <FilterChip key={"b"+id} label={brands.find(x=>x.id===id)?.name || id} onClear={() => toggle(setBrandFilter)(id)} />)}
          {[...pmFilter].map(id => <FilterChip key={"pm"+id} label={"PM: " + (members.find(m=>(m.user_id||m.id)===id)?.name || members.find(m=>(m.user_id||m.id)===id)?.email || id)} onClear={() => toggle(setPmFilter)(id)} />)}
          {[...assigneeFilter].map(id => <FilterChip key={"a"+id} label={data?.by_assignee?.find(x=>x.key===id)?.label || members.find(m=> (m.user_id||m.id)===id)?.name || id} onClear={() => toggle(setAssigneeFilter)(id)} />)}
          {[...projectFilter].map(id => <FilterChip key={"p"+id} label={data?.by_project?.find(x=>x.key===id)?.label || projects.find(p=>p.id===id)?.name || id} onClear={() => toggle(setProjectFilter)(id)} />)}
          {[...priorityFilter].map(id => <FilterChip key={"pr"+id} label={id} onClear={() => toggle(setPriorityFilter)(id)} />)}
          {[...timingFilter].map(id => <FilterChip key={"t"+id} label={TIMING_LABEL[id] || id} onClear={() => toggle(setTimingFilter)(id)} />)}
          {[...effortFilter].map(id => <FilterChip key={"e"+id} label={EFFORT_LABEL[id] || id} onClear={() => toggle(setEffortFilter)(id)} />)}
          <button type="button" className="text-xs underline text-muted-foreground hover:text-foreground" onClick={clearFilters} data-testid="clear-all-filters">Clear all</button>
        </div>
      )}

      {/* Task list controls */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <form onSubmit={onSearchSubmit} className="flex-1 min-w-[220px] relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search tasks or timesheet notes…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8" data-testid="report-search" />
        </form>
        <label className="text-xs text-muted-foreground">Group by</label>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-36 h-8" data-testid="report-groupby"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="assignee_name">Assignee</SelectItem>
            <SelectItem value="project_name">Project</SelectItem>
            <SelectItem value="client_name">Client</SelectItem>
            <SelectItem value="brand_name">Brand</SelectItem>
            <SelectItem value="department_name">Department</SelectItem>
            <SelectItem value="status_label">Status</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
            <SelectItem value="timing">Timing</SelectItem>
            <SelectItem value="effort_status">Effort</SelectItem>
          </SelectContent>
        </Select>
        {/* R268gggg · Feb 2026 — Optional secondary grouping inside
            each COMPLETED / IN PROGRESS section. */}
        <label className="text-xs text-muted-foreground">Then by</label>
        <Select value={subGroupBy} onValueChange={setSubGroupBy}>
          <SelectTrigger className="w-32 h-8" data-testid="report-subgroupby"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="project_name">Project</SelectItem>
            <SelectItem value="client_name">Client</SelectItem>
            <SelectItem value="brand_name">Brand</SelectItem>
            <SelectItem value="assignee_name">Assignee</SelectItem>
            <SelectItem value="department_name">Department</SelectItem>
            <SelectItem value="status_label">Status</SelectItem>
            <SelectItem value="priority">Priority</SelectItem>
          </SelectContent>
        </Select>
        {groups && groups.length > 0 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 rounded-none" onClick={() => setCollapsedGroups(new Set())} data-testid="report-expand-all">Expand all</Button>
            <Button variant="outline" size="sm" className="h-8 rounded-none" onClick={() => setCollapsedGroups(new Set(groups.map(([g]) => g || "—")))} data-testid="report-collapse-all">Collapse all</Button>
            {subGroupBy !== "none" && (
              <>
                <Button variant="outline" size="sm" className="h-8 rounded-none" onClick={() => setCollapsedSubs(new Set())} data-testid="report-expand-subs">Expand subs</Button>
                <Button variant="outline" size="sm" className="h-8 rounded-none" onClick={() => {
                  // Collapse every sub-key that currently exists in
                  // the rendered dataset (status section + nested
                  // secondary group + date row when applicable).
                  const next = new Set();
                  for (const [grp, tasks] of groups) {
                    const pk = grp || "—";
                    const subKeys = new Set(tasks.map((t) => t[subGroupBy] || "—"));
                    for (const sk of subKeys) {
                      next.add(`${pk}::completed::${sk}`);
                      next.add(`${pk}::progress::${sk}`);
                    }
                    next.add(`${pk}::completed`);
                    next.add(`${pk}::progress`);
                  }
                  setCollapsedSubs(next);
                }} data-testid="report-collapse-subs">Collapse subs</Button>
              </>
            )}
          </div>
        )}
        {/* R268~10 · Feb 2026 — Mobile-only layout toggle. Segment
            control is invisible ≥sm because the desktop table is
            already the canonical layout. On phones users can flip
            between the compact table and the stacked-card view.*/}
        <div className="sm:hidden flex items-center border border-border rounded-none" role="tablist" aria-label="Mobile layout">
          <button
            type="button"
            onClick={() => setMobileView("table")}
            className={`text-[10px] font-semibold px-2 py-1 uppercase tracking-widest ${mobileView === "table" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="report-mobile-view-table"
            aria-pressed={mobileView === "table"}
          >Table</button>
          <button
            type="button"
            onClick={() => setMobileView("cards")}
            className={`text-[10px] font-semibold px-2 py-1 uppercase tracking-widest border-l border-border ${mobileView === "cards" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="report-mobile-view-cards"
            aria-pressed={mobileView === "cards"}
          >Cards</button>
        </div>
        {/* R268~11 · Feb 2026 — Compact sort chip (mobile-only, cards
            view only). Rebinds to the same sortBy / sortDir state the
            desktop toolbar uses, so switching between views preserves
            the order. Tapping the active chip flips direction. */}
        {mobileView === "cards" && (
          <MobileSortChip
            sortBy={sortBy}
            sortDir={sortDir}
            onChange={(col) => {
              if (col === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
              else { setSortBy(col); setSortDir("asc"); }
            }}
          />
        )}
        {/* R268~11 · Feb 2026 — Cards export. Snapshots the mobile
            cards container to a PNG via html2canvas, then either
            invokes the Web Share API (WhatsApp / Slack / mail sheet
            on phones) or falls back to a plain download. */}
        {mobileView === "cards" && (
          <button
            type="button"
            onClick={() => exportCardsToImage({ dateFrom, dateTo, setBusy: setExportBusy })}
            disabled={exportBusy}
            className="sm:hidden text-[10px] font-semibold uppercase tracking-widest px-2 py-1 border border-border rounded-none bg-background hover:bg-muted flex items-center gap-1 disabled:opacity-50"
            data-testid="report-cards-export-btn"
            title="Share cards as PNG (WhatsApp-friendly)"
          >
            {exportBusy ? "…" : (<><Share2 className="w-3 h-3" /> Share</>)}
          </button>
        )}
      </div>

      {/* R268~20 · Feb 2026 — Hours by <Group by> bar chart. Sits above
          the table so managers get a visual heat-map of where hours
          landed BEFORE they scroll into the raw rows. Reacts to every
          filter / date change because it derives from the same `groups`
          the table renders from. */}
      {groupHoursChart && groupHoursChart.length > 0 && (
        <div className="mb-4 border border-border bg-background p-3" data-testid="report-group-hours-chart">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Hours by {groupByLabel}
            </span>
            <span className="text-[10px] font-mono text-muted-foreground">
              {groupHoursChart.length} {groupHoursChart.length === 1 ? "row" : "rows"} · {groupHoursChart.reduce((s, r) => s + r.total, 0).toFixed(1)}h
            </span>
          </div>
          <div style={{ width: "100%", height: Math.max(140, Math.min(28 * groupHoursChart.length + 40, 520)) }}>
            <ResponsiveContainer>
              <BarChart data={groupHoursChart} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 10 }}
                  stroke="hsl(var(--muted-foreground))"
                  interval={0}
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                  contentStyle={{
                    fontSize: "11px",
                    background: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 0,
                  }}
                  formatter={(v, name) => [`${Number(v).toFixed(1)}h`, name === "done" ? "Completed" : "In progress"]}
                />
                <Legend
                  wrapperStyle={{ fontSize: "10px", paddingTop: "4px" }}
                  formatter={(value) => value === "done" ? "Completed" : "In progress"}
                />
                <Bar dataKey="done" stackId="h" fill="#10b981" name="done" />
                <Bar dataKey="progress" stackId="h" fill="#3b82f6" name="progress" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* R268~27 · Feb 2026 — Quick type-to-filter above the table +
          cards. Live, purely client-side (no reload) — matches against
          title, notes, assignee, project, client, brand, department,
          priority, status label, due date (raw + readable), completed
          date. Grouping structure is preserved: parent / date / status
          / sub headers only stay visible if at least one child
          matches. */}
      <div className="flex items-center gap-2 mb-2" data-testid="report-quickfilter-wrapper">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Type to filter rows — matches title, notes, assignee, project…"
            value={quickFilter}
            onChange={(e) => setQuickFilter(e.target.value)}
            className="pl-8 h-8 text-[13px]"
            data-testid="report-quickfilter"
          />
          {quickFilter && (
            <button
              type="button"
              onClick={() => setQuickFilter("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground"
              data-testid="report-quickfilter-clear"
              title="Clear filter"
            >Clear</button>
          )}
        </div>
        {quickFilter.trim() && (
          <span
            className="text-[11px] font-mono text-muted-foreground shrink-0"
            data-testid="report-quickfilter-count"
          >
            {filteredTasks.length} of {sortedTasks.length} tasks
          </span>
        )}
      </div>

      {/* Task list — single table with sortable headers and expandable
          group header rows.  Feb 2026 rewrite: one <table> for the whole
          list means a click-drag select of tasks works, columns line up
          across groups, and vertical density is much tighter. */}
      <div className="border border-border" data-testid="report-tasks-list">
        {(!groups && filteredTasks.length === 0) || (groups && groups.length === 0) ? (
          <div className="p-8 text-center text-sm text-muted-foreground italic">
            {quickFilter.trim()
              ? `No rows match “${quickFilter.trim()}”. Try a different keyword or clear the filter.`
              : "No completed tasks match the current filters."}
          </div>
        ) : (
          <>
            {/* R268~10 · Feb 2026 — Table view: always visible ≥ sm; on
                mobile it renders only when the toggle is "table". */}
            <div className={mobileView === "cards" ? "hidden sm:block" : ""}>
              <UnifiedTasksTable
                groups={groups}
                flatTasks={groups ? null : filteredTasks}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortChange={(col) => {
                  if (col === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
                  else { setSortBy(col); setSortDir("asc"); }
                }}
                collapsed={collapsedGroups}
                onToggleGroup={(g) => {
                  const next = new Set(collapsedGroups);
                  if (next.has(g)) next.delete(g); else next.add(g);
                  setCollapsedGroups(next);
                }}
                onOpenTask={openTaskById}
                wsId={activeWorkspaceId}
                dateFrom={dateFrom}
                dateTo={dateTo}
                groupBy={groupBy}
                subGroupBy={subGroupBy}
                collapsedSubs={collapsedSubs}
                onToggleSub={(k) => {
                  const next = new Set(collapsedSubs);
                  if (next.has(k)) next.delete(k); else next.add(k);
                  setCollapsedSubs(next);
                }}
                onSetCollapsedSubs={setCollapsedSubs}
              />
            </div>
            {/* R268~10 · Card view: mobile-only, and only when toggle
                is "cards". Groups roll up into simple section headers
                so the collapse controls still work. */}
            {mobileView === "cards" && (
              <div className="sm:hidden">
                <MobileCardsList
                  groups={groups}
                  flatTasks={groups ? null : filteredTasks}
                  collapsed={collapsedGroups}
                  onToggleGroup={(g) => {
                    const next = new Set(collapsedGroups);
                    if (next.has(g)) next.delete(g); else next.add(g);
                    setCollapsedGroups(next);
                  }}
                  onOpenTask={openTaskById}
                  groupBy={groupBy}
                  subGroupBy={subGroupBy}
                  collapsedSubs={collapsedSubs}
                  onToggleSub={(k) => {
                    const next = new Set(collapsedSubs);
                    if (next.has(k)) next.delete(k); else next.add(k);
                    setCollapsedSubs(next);
                  }}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                />
              </div>
            )}
          </>
        )}
      </div>

      {openTask && (
        <TaskDetail
          task={openTask}
          members={members}
          onClose={() => setOpenTask(null)}
          onPatch={patchOpenTask}
        />
      )}

      {/* Iter Feb2026 — Roster changes chip on the report header. Answers
          "why does the row count drift" by naming who joined / exited /
          is on notice during the picked range. Sits right above Silent
          Members. */}
      <RosterChangesSection
        wsId={activeWorkspaceId}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />

      {/* Feb 2026 — Silent members section: workspace members with ZERO
          completed tasks in the selected range. Lazy-fetched so it never
          slows down the main report. R268zzz · Feb 2026 — Anyone whose
          user_id appears in the main data table for the current range
          is filtered out client-side so the two panels never disagree. */}
      <SilentMembersSection
        wsId={activeWorkspaceId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        pmFilter={pmFilter}
        excludeUserIds={visibleAssigneeIds}
      />

      {showSettings && (
        <DailyReportSettingsDialog
          wsId={activeWorkspaceId}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "text-foreground", testid }) {
  return (
    <div className="border border-border rounded-md p-4 bg-background" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-3xl font-bold ${tone} leading-tight mt-1`}>{value ?? 0}</div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="border border-border rounded-md p-4 bg-background" data-testid={testid}>
      <div className="text-sm font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1">
      {label}
      <button type="button" onClick={onClear} className="ml-1 hover:bg-background rounded p-0.5" aria-label="Clear">
        <X className="w-2.5 h-2.5" />
      </button>
    </Badge>
  );
}

function TasksTable({ tasks }) {
  return null; // Legacy component removed — see UnifiedTasksTable below.
}

// Feb 2026 — Unified table renders both the flat sorted list and the
// grouped-by-any-column view without spawning a separate <table> per
// group. Column widths align across group boundaries, click-drag select
// works, and the sticky header stays put when the report scrolls.
function UnifiedTasksTable({ groups, flatTasks, sortBy, sortDir, onSortChange, collapsed, onToggleGroup, onOpenTask, wsId, dateFrom, dateTo, groupBy, subGroupBy = "none", collapsedSubs = new Set(), onToggleSub = () => {}, onSetCollapsedSubs = () => {} }) {
  const fmt = (v) => (v ? String(v).slice(0, 10) : "—");
  const sumHours = (arr, key) => arr.reduce((s, t) => s + (Number(t[key]) || 0), 0);
  // R268zzz · Feb 2026 — Grid-line class applied to every cell so the
  // table reads like a spreadsheet even on high-density rows.
  const cell = "border border-border/70";
  // R268ffff · Feb 2026 — Enable date grouping whenever the user is
  // grouping by Assignee AND at least one date is picked (works for
  // single-date too — previously we required a multi-day range which
  // meant single-date reports summed `actual_hours` grand totals
  // even when only a slice of those hours fell inside the window).
  const dateGroupEnabled = groupBy === "assignee_name" && !!dateFrom && !!dateTo;
  // Build the list of days [from, to] inclusive so empty days can be
  // suppressed without recomputing the range for each employee row.
  const rangeDates = useMemo(() => {
    if (!dateGroupEnabled) return [];
    const out = [];
    const a = new Date(dateFrom); const b = new Date(dateTo);
    for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [dateGroupEnabled, dateFrom, dateTo]);
  // Per-task hours-for-a-specific-date (0 when the task has no time
  // entry on that day even if it was completed then).
  const hoursOn = (task, dateStr) => {
    const arr = Array.isArray(task.hours_by_date) ? task.hours_by_date : [];
    for (const e of arr) { if (e.date === dateStr) return Number(e.hours) || 0; }
    return 0;
  };
  // R268ffff · Feb 2026 — Sum every hours_by_date entry that falls
  // inside the report window for a task. Replaces `t.actual_hours`
  // (grand total) in the employee-group subtotal when date-grouping
  // is active so the visible number matches the sum of per-date rows.
  const rangeHours = (task) => {
    const arr = Array.isArray(task.hours_by_date) ? task.hours_by_date : [];
    let s = 0; for (const e of arr) s += Number(e.hours) || 0; return s;
  };

  // R265 · Feb 2026 — Reordered so effort + date columns lead and the
  // people/project columns trail, matching how PMs scan the report.
  // R268~9 · Feb 2026 — Compact mobile pass. `sm:` breakpoints hide
  // low-priority columns (Due, Completed, Timing, Effort, Client,
  // Brand, Project, Assignee) on phones, priority icon moves inline
  // with the title (dedicated column collapses), Notes column
  // migrates to sit right after Δ% so effort context stays adjacent.
  const renderRow = (t, hoursCol = null) => (
    <tr key={`${t.id}-${hoursCol ?? "all"}`} className="border-t border-border hover:bg-muted/30" data-testid={`report-task-${t.id}`}>
      <td className={`${cell} px-1 sm:px-2 py-0.5 sm:py-1 font-medium max-w-[240px]`}>
        <div className="flex items-center gap-1 sm:gap-1.5">
          {/* R268~9 · Status: icon on mobile, chip on desktop. */}
          {t.kind === "progress" ? (
            <CircleDashed
              className="w-3.5 h-3.5 shrink-0 text-blue-600 sm:hidden"
              title="In progress today"
              data-testid={`report-task-status-icon-${t.id}`}
            />
          ) : (
            <CheckCircle2
              className="w-3.5 h-3.5 shrink-0 text-emerald-600 sm:hidden"
              title="Completed today"
              data-testid={`report-task-status-icon-${t.id}`}
            />
          )}
          <span className={`hidden sm:inline-block text-[9px] px-1 py-[1px] font-semibold border shrink-0 ${
            t.kind === "progress"
              ? "bg-blue-500/10 text-blue-700 border-blue-500/40"
              : "bg-emerald-500/10 text-emerald-700 border-emerald-500/40"
          }`} title={t.kind === "progress" ? "In progress today" : "Completed today"}>
            {t.kind === "progress" ? "PROG" : "DONE"}
          </span>
          {/* R268~9 · Priority icon inline before the title on mobile
              (its dedicated column is hidden < sm). On desktop the
              inline icon hides so the dedicated column stays canonical. */}
          <PriorityIcon
            priority={t.priority || "medium"}
            className="w-3 h-3 shrink-0 sm:hidden"
            title={`Priority: ${t.priority || "medium"}`}
          />
          <button
            type="button"
            onClick={() => onOpenTask?.(t.id)}
            className="text-left hover:text-primary hover:underline truncate flex-1 text-xs sm:text-sm"
            title={t.title}
            data-testid={`report-task-title-${t.id}`}
          >{t.title}</button>
        </div>
      </td>
      <td className={`${cell} px-2 py-1 text-center hidden sm:table-cell`} title={t.priority ? `Priority: ${t.priority}` : "Priority"}>
        <div className="flex items-center justify-center">
          <PriorityIcon priority={t.priority || "medium"} className="w-3.5 h-3.5" />
        </div>
      </td>
      <td className={`${cell} px-1 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-xs text-right whitespace-nowrap`}>{hoursFmt(t.est_hours)}</td>
      <td
        className={`${cell} px-1 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-xs text-right whitespace-nowrap font-semibold`}
        title={hoursCol != null ? `Hours this date · Grand total: ${hoursFmt(t.actual_hours)}` : "Actual hours"}
      >
        {hoursCol != null ? (
          <span data-testid={`report-task-hours-split-${t.id}`}>
            <span className="text-blue-700">{hoursFmt(hoursCol)}</span>
            <span className="text-muted-foreground/70 mx-0.5">/</span>
            <span className="text-foreground/80 text-[10px] font-normal">{hoursFmt(t.actual_hours)}</span>
          </span>
        ) : hoursFmt(t.actual_hours)}
      </td>
      <td className={`${cell} px-1 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-xs text-right whitespace-nowrap font-medium`}>{varFmt(t)}</td>
      {/* R268~9 · Feb 2026 — Notes column relocated to sit right after
          Δ% (was at the very end). Timesheet notes usually explain
          effort variance so keeping them adjacent makes the row read
          left-to-right as a coherent story. */}
      <td className={`${cell} px-1 sm:px-2 py-0.5 sm:py-1 text-xs max-w-[220px]`}>
        {Array.isArray(t.notes) && t.notes.length > 0 ? (
          <div className="truncate italic text-muted-foreground"
               title={t.notes.map((n) => `${n.date} · ${n.hours.toFixed(1)}h — ${n.note}`).join("\n\n")}
               data-testid={`report-task-notes-${t.id}`}>
            <span className="text-foreground/80 not-italic">“</span>
            {t.notes[0].note}
            <span className="text-foreground/80 not-italic">”</span>
            {t.notes.length > 1 && (
              <span className="text-foreground/60 not-italic ml-1">+{t.notes.length - 1}</span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>
      <td
        className={`${cell} px-2 py-1 text-xs whitespace-nowrap hidden sm:table-cell`}
        title={t.due_date ? fmtNiceDate(t.due_date) : undefined}
      >
        {t.due_date ? (
          <span>
            {fmtNiceDate(t.due_date)}
            {t.timing === "delayed" && (() => {
              const dd = daysDelayed(t.due_date, t.completed_at);
              return dd != null && dd > 0
                ? <span className="ml-1 text-rose-600 font-semibold text-[10px]" data-testid={`report-task-delay-${t.id}`}>· {dd}d late</span>
                : null;
            })()}
          </span>
        ) : "—"}
      </td>
      <td
        className={`${cell} px-2 py-1 text-xs whitespace-nowrap hidden sm:table-cell`}
        title={t.completed_at ? fmtNiceDate(t.completed_at) : undefined}
      >{fmtNiceDate(t.completed_at)}</td>
      <td className={`${cell} px-2 py-1 text-xs font-medium whitespace-nowrap hidden sm:table-cell`} style={{ color: TIMING_COLOR[t.timing] }}>{TIMING_LABEL[t.timing]}</td>
      <td className={`${cell} px-2 py-1 text-xs font-medium whitespace-nowrap hidden sm:table-cell`} style={{ color: EFFORT_COLOR[t.effort_status] }}>{EFFORT_LABEL[t.effort_status]}</td>
      <td className={`${cell} px-2 py-1 text-xs truncate max-w-[110px] hidden sm:table-cell`} title={t.client_name}>{t.client_name}</td>
      <td className={`${cell} px-2 py-1 text-xs truncate max-w-[110px] hidden sm:table-cell`} title={t.brand_name}>{t.brand_name}</td>
      <td className={`${cell} px-2 py-1 text-xs truncate max-w-[130px] hidden sm:table-cell`} title={t.project_name}>{t.project_name}</td>
      <td className={`${cell} px-2 py-1 text-xs truncate max-w-[130px] hidden sm:table-cell`} title={t.assignee_name}>
        <UserTimesheetHover wsId={wsId} userId={t.assignee_id} dateFrom={dateFrom} dateTo={dateTo} disabled={!t.assignee_id}>
          <span className="cursor-help hover:underline">{t.assignee_name}</span>
        </UserTimesheetHover>
      </td>
    </tr>
  );

  // R268gggg · Feb 2026 — All facts (badge / label / count / hours /
  // % of total) hug the left edge inside a `sticky left-4` wrapper so
  // the whole line stays visible when the table is scrolled
  // horizontally on mobile. `pct` (optional) shows what share of the
  // report's total hours this section accounts for.
  const renderSubHeaderRow = (label, kind, count, hours, testKey, pct = null, collapsibleKey = null, indent = 4) => {
    const collapsed = collapsibleKey && collapsedSubs.has(collapsibleKey);
    const clickable = !!collapsibleKey;
    const Chevron = collapsed ? ChevronRight : ChevronDown;
    return (
      <tr className="bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200/60 dark:border-slate-700/40" data-testid={`report-subgroup-${testKey}`}>
        <td colSpan={14} className={`${cell} px-4 py-1`}>
          <div className={`sticky flex items-center gap-2 text-xs font-bold flex-wrap max-w-[calc(100vw-32px)] sm:max-w-none ${indent === 8 ? "left-8" : "left-4"}`}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onToggleSub(collapsibleKey)}
                className="inline-flex items-center gap-1 hover:text-primary shrink-0"
                data-testid={`report-subgroup-toggle-${testKey}`}
              >
                <Chevron className="w-3 h-3" />
              </button>
            ) : null}
            <span className={`text-[9px] px-1 py-[1px] font-semibold border shrink-0 ${
              kind === "progress"
                ? "bg-blue-500/10 text-blue-700 border-blue-500/40"
                : kind === "completed"
                  ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/40"
                  : "bg-slate-500/10 text-slate-700 border-slate-500/40"
            }`}>{kind === "progress" ? "IN PROGRESS" : kind === "completed" ? "COMPLETED" : "—"}</span>
            {label ? <span className="text-muted-foreground truncate max-w-[10rem] sm:max-w-none">{label}</span> : null}
            <span className="flex items-center gap-2 sm:gap-3 text-[11px] text-muted-foreground font-mono">
              <span>{count} task{count === 1 ? "" : "s"}</span>
              <span aria-hidden className="opacity-60">·</span>
              <span className="text-foreground font-semibold">{hours.toFixed(1)}h</span>
              {pct != null && (
                <>
                  <span aria-hidden className="opacity-60">·</span>
                  <span className="text-foreground/80 font-semibold">{pct.toFixed(0)}%</span>
                </>
              )}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  // R268gggg · Feb 2026 — Header for a Project/Client/... sub-group
  // nested inside a COMPLETED / IN PROGRESS section. Indent by 8 so it
  // reads as one level deeper than the status header.
  const renderNestedSubRow = (subLabel, count, hours, testKey, pct, collapsibleKey) => {
    const collapsed = collapsibleKey && collapsedSubs.has(collapsibleKey);
    const Chevron = collapsed ? ChevronRight : ChevronDown;
    return (
      <tr className="bg-slate-50/60 dark:bg-slate-800/20 border-t border-slate-200/50 dark:border-slate-700/30" data-testid={`report-nested-${testKey}`}>
        <td colSpan={14} className={`${cell} px-4 py-1`}>
          <div className="sticky left-8 flex items-center gap-2 text-xs font-bold flex-wrap max-w-[calc(100vw-40px)] sm:max-w-none">
            <button
              type="button"
              onClick={() => onToggleSub(collapsibleKey)}
              className="inline-flex items-center gap-1 hover:text-primary shrink-0"
              data-testid={`report-nested-toggle-${testKey}`}
            >
              <Chevron className="w-3 h-3" />
            </button>
            <span className="text-muted-foreground truncate max-w-[12rem] sm:max-w-none" title={subLabel}>{subLabel || "—"}</span>
            <span className="flex items-center gap-2 sm:gap-3 text-[10px] text-muted-foreground font-mono">
              <span>{count} task{count === 1 ? "" : "s"}</span>
              <span aria-hidden className="opacity-60">·</span>
              <span className="text-foreground font-semibold">{hours.toFixed(1)}h</span>
              {pct != null && (
                <>
                  <span aria-hidden className="opacity-60">·</span>
                  <span className="text-foreground/80 font-semibold">{pct.toFixed(0)}%</span>
                </>
              )}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  // Renders one COMPLETED / IN PROGRESS status section: header row +
  // (optional) nested Project/Client/… sub-groups + task rows.
  // `hoursFn(task)` returns the hours attributed to the task for this
  // section (grand-total actual_hours OR the per-date slice when
  // date-grouping is active).
  const renderStatusSection = (parentKey, kind, tasks, hoursFn, labelPrefix, testKeyPrefix, reportTotalHours) => {
    const sectionHours = tasks.reduce((s, t) => s + hoursFn(t), 0);
    const pct = reportTotalHours > 0 ? (sectionHours / reportTotalHours) * 100 : null;
    const statusKey = `${parentKey}::${kind}${labelPrefix ? "::" + labelPrefix : ""}`;
    const statusCollapsed = collapsedSubs.has(statusKey);
    const rows = [];
    rows.push(
      <React.Fragment key={`${testKeyPrefix}-hdr`}>
        {renderSubHeaderRow(labelPrefix || null, kind, tasks.length, sectionHours, testKeyPrefix, pct, statusKey, 4)}
      </React.Fragment>
    );
    if (statusCollapsed) return rows;
    // Effective secondary grouping: skip when subGroupBy is 'none' or
    // matches the primary groupBy (would produce a single sub-group).
    const effSub = (subGroupBy && subGroupBy !== "none" && subGroupBy !== groupBy) ? subGroupBy : null;
    if (!effSub) {
      // Flat task rows inside this status section.
      for (const t of tasks) rows.push(<React.Fragment key={`${testKeyPrefix}-t-${t.id}`}>{renderRow(t, hoursFn(t))}</React.Fragment>);
      return rows;
    }
    // Group tasks by the secondary field, preserving insertion order.
    const subMap = new Map();
    for (const t of tasks) {
      const sk = t[effSub] || (effSub === "priority" ? "medium" : "—");
      if (!subMap.has(sk)) subMap.set(sk, []);
      subMap.get(sk).push(t);
    }
    // Sort sub-groups by hours desc for scannability.
    const subEntries = [...subMap.entries()].sort((a, b) => b[1].reduce((s, t) => s + hoursFn(t), 0) - a[1].reduce((s, t) => s + hoursFn(t), 0));
    for (const [subKey, subTasks] of subEntries) {
      const subHours = subTasks.reduce((s, t) => s + hoursFn(t), 0);
      const subPct = reportTotalHours > 0 ? (subHours / reportTotalHours) * 100 : null;
      const nestedKey = `${statusKey}::${subKey}`;
      const nestedCollapsed = collapsedSubs.has(nestedKey);
      const testKey = `${testKeyPrefix}-${String(subKey).replace(/\s+/g, "_")}`;
      rows.push(
        <React.Fragment key={testKey}>
          {renderNestedSubRow(subKey, subTasks.length, subHours, testKey, subPct, nestedKey)}
          {!nestedCollapsed && subTasks.map((t) => (
            <React.Fragment key={`${testKey}-t-${t.id}`}>{renderRow(t, hoursFn(t))}</React.Fragment>
          ))}
        </React.Fragment>
      );
    }
    return rows;
  };

  const totals = useMemo(() => {
    const src = flatTasks || (groups ? groups.flatMap(([, arr]) => arr) : []);
    const doneRows = src.filter((t) => t.kind !== "progress");
    const progRows = src.filter((t) => t.kind === "progress");
    const activeIds = new Set(src.map((t) => t.assignee_id).filter(Boolean));
    return {
      doneCount: doneRows.length,
      doneHours: sumHours(doneRows, "actual_hours"),
      progCount: progRows.length,
      progHours: sumHours(progRows, "actual_hours"),
      totalHours: sumHours(src, "actual_hours"),
      totalEst: sumHours(src, "est_hours"),
      activeCount: activeIds.size,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatTasks, groups]);

  // R268gggg · Feb 2026 — Total hours across every visible task,
  // adapted for date-grouping (uses rangeHours instead of grand total).
  // Used by status/nested sub-headers to compute "% of total".
  const reportTotalHours = useMemo(() => {
    const src = flatTasks || (groups ? groups.flatMap(([, arr]) => arr) : []);
    return dateGroupEnabled
      ? src.reduce((s, t) => s + rangeHours(t), 0)
      : sumHours(src, "actual_hours");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatTasks, groups, dateGroupEnabled]);

  // Bulk expand/collapse subs FOR A SINGLE PARENT — used by the tiny
  // icon buttons next to each parent-group header chevron.
  const perParentSubKeys = (parentKey, tasks) => {
    const set = new Set();
    if (dateGroupEnabled) {
      for (const d of rangeDates) {
        set.add(`${parentKey}::date::${d}`);
        set.add(`${parentKey}::completed::${d}`);
        set.add(`${parentKey}::progress::${d}`);
        if (subGroupBy !== "none" && subGroupBy !== groupBy) {
          const subKeys = new Set(tasks.map((t) => t[subGroupBy] || "—"));
          for (const sk of subKeys) {
            set.add(`${parentKey}::completed::${d}::${sk}`);
            set.add(`${parentKey}::progress::${d}::${sk}`);
          }
        }
      }
    } else {
      set.add(`${parentKey}::completed`);
      set.add(`${parentKey}::progress`);
      if (subGroupBy !== "none" && subGroupBy !== groupBy) {
        const subKeys = new Set(tasks.map((t) => t[subGroupBy] || "—"));
        for (const sk of subKeys) {
          set.add(`${parentKey}::completed::${sk}`);
          set.add(`${parentKey}::progress::${sk}`);
        }
      }
    }
    return set;
  };

  return (
    // R268zzz · Feb 2026 — Constrain height + enable vertical scroll on
    // the table wrapper so the sticky <thead> actually freezes while
    // the body scrolls. `min-h-[420px]` avoids a jump when only a few
    // rows are present.
    <div className="overflow-auto -mx-2 px-2 sm:mx-0 sm:px-0 border border-border max-h-[70vh]" data-testid="report-tasks-scroll">
      <table className="w-full text-sm sm:min-w-[1180px] border-collapse report-grid">
        <thead>
          <tr className="text-[10px] sm:text-[10px] uppercase tracking-widest text-muted-foreground bg-muted sticky top-0 z-20">
            <SortHead label="Task" col="title" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} align="left" />
            {/* R268~9 · Priority column hidden on mobile (icon inline with title). */}
            <SortHead label="!" col="priority" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-8" align="center" className="hidden sm:table-cell" />
            <SortHead label="Est" col="est_hours" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-14" align="right" />
            <SortHead label="Actual" col="actual_hours" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-14" align="right" />
            <SortHead label="Δ%" col="effort_variance_pct" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-16" align="right" />
            {/* R268~9 · Notes now sits right after Δ% (was last column). */}
            <th className="px-1 sm:px-2 py-1 text-left font-semibold uppercase text-[10px] tracking-widest w-56 border border-border/70 bg-muted">Notes</th>
            <SortHead label="Due" col="due_date" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-20" className="hidden sm:table-cell" />
            <SortHead label="Completed" col="completed_at" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-20" className="hidden sm:table-cell" />
            <SortHead label="Timing" col="timing" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-16" className="hidden sm:table-cell" />
            <SortHead label="Effort" col="effort_status" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-16" className="hidden sm:table-cell" />
            <SortHead label="Client" col="client_name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-28" className="hidden sm:table-cell" />
            <SortHead label="Brand" col="brand_name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-28" className="hidden sm:table-cell" />
            <SortHead label="Project" col="project_name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-32" className="hidden sm:table-cell" />
            <SortHead label="Assignee" col="assignee_name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} width="w-32" className="hidden sm:table-cell" />
          </tr>
        </thead>
        <tbody>
          {flatTasks && flatTasks.map((t) => renderRow(t))}
          {groups && groups.map(([grp, tasks]) => {
            const key = grp || "—";
            const isCollapsed = collapsed.has(key);
            const done = tasks.filter((x) => x.kind !== "progress");
            const prog = tasks.filter((x) => x.kind === "progress");
            // R268ffff · Feb 2026 — When date-grouping is on, subtotal
            // must sum ONLY the hours logged inside the report window
            // (from `hours_by_date`), otherwise it uses each task's
            // grand-total `actual_hours`. That way the employee header
            // Σ matches the sum of per-date rows below it.
            const doneH = dateGroupEnabled ? done.reduce((s, x) => s + rangeHours(x), 0) : sumHours(done, "actual_hours");
            const progH = dateGroupEnabled ? prog.reduce((s, x) => s + rangeHours(x), 0) : sumHours(prog, "actual_hours");
            const totalH = doneH + progH;
            return (
              <React.Fragment key={key}>
                <tr className="bg-slate-200/80 dark:bg-slate-800/80 border-t-2 border-slate-300 dark:border-slate-700" data-testid={`report-group-${key}`}>
                  <td colSpan={14} className={`${cell} px-2 py-1.5`}>
                    {/* R268aaaa · Feb 2026 — Sticky-left wrapper so on
                        mobile (where the table has min-w:1180px and
                        scrolls horizontally) the employee name AND
                        their day + overall subtotals stay visible at
                        the left edge of the viewport instead of
                        floating out at ~1000px in. */}
                    <div className="sticky left-2 flex items-center gap-1.5 text-[15px] font-bold flex-wrap max-w-[calc(100vw-24px)] sm:max-w-none" data-testid={`report-group-name-${key}`}>
                      <button
                        type="button"
                        onClick={() => onToggleGroup(key)}
                        className="inline-flex items-center gap-1.5 hover:text-primary text-left"
                        data-testid={`report-group-toggle-${key}`}
                      >
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
                        <span className="truncate max-w-[10rem] sm:max-w-none">{grp || "—"}</span>
                      </button>
                      <span className="flex items-center gap-2 sm:gap-3 text-xs text-muted-foreground font-mono font-normal">
                        <span>{done.length} done</span>
                        <span aria-hidden className="opacity-60">·</span>
                        <span>{prog.length} in-progress</span>
                        <span aria-hidden className="opacity-60">·</span>
                        <span className="text-foreground font-semibold" data-testid={`report-group-hours-${key}`}>{totalH.toFixed(1)}h</span>
                        {reportTotalHours > 0 && (
                          <>
                            <span aria-hidden className="opacity-60">·</span>
                            <span className="text-foreground/80 font-semibold" data-testid={`report-group-pct-${key}`}>{((totalH / reportTotalHours) * 100).toFixed(0)}%</span>
                          </>
                        )}
                      </span>
                      {/* R268gggg · Feb 2026 — Per-parent expand/collapse
                          subs. Only visible when secondary grouping is
                          active OR date-grouping is on (there's
                          something to collapse under the parent). */}
                      {!isCollapsed && (subGroupBy !== "none" || dateGroupEnabled) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal">
                          <button
                            type="button"
                            onClick={() => {
                              const keys = perParentSubKeys(key, tasks);
                              const next = new Set(collapsedSubs);
                              for (const k of keys) next.delete(k);
                              onSetCollapsedSubs(next);
                            }}
                            className="px-1.5 py-[1px] border border-border/70 hover:bg-muted rounded-none text-muted-foreground hover:text-foreground"
                            data-testid={`report-group-expand-subs-${key}`}
                            title="Expand every sub-group under this parent"
                          >Expand subs</button>
                          <button
                            type="button"
                            onClick={() => {
                              const keys = perParentSubKeys(key, tasks);
                              const next = new Set(collapsedSubs);
                              for (const k of keys) next.add(k);
                              onSetCollapsedSubs(next);
                            }}
                            className="px-1.5 py-[1px] border border-border/70 hover:bg-muted rounded-none text-muted-foreground hover:text-foreground"
                            data-testid={`report-group-collapse-subs-${key}`}
                            title="Collapse every sub-group under this parent"
                          >Collapse subs</button>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                {!isCollapsed && dateGroupEnabled && (
                  // R268zzz · Feb 2026 — Date-wise sub-sections. For
                  // each date in the range, show three buckets:
                  //   A) Tasks the person COMPLETED on this date
                  //   B) Tasks with time LOGGED on this date but not
                  //      completed that date (may be completed later or
                  //      not at all yet)
                  //   C) Nothing → skip the row entirely.
                  // Hours attributed to a date are ALWAYS the time-entry
                  // hours on that date (from `hours_by_date`), never the
                  // task's grand total. That way the sum of per-date
                  // rows matches the timesheet total for the day.
                  rangeDates.map((d) => {
                    const completedToday = tasks.filter((x) => (x.completed_at || "").slice(0, 10) === d);
                    const loggedToday = tasks.filter((x) => hoursOn(x, d) > 0 && (x.completed_at || "").slice(0, 10) !== d);
                    if (completedToday.length === 0 && loggedToday.length === 0) return null;
                    const dayTotal = tasks.reduce((s, x) => s + hoursOn(x, d), 0);
                    // R268hhhh · Feb 2026 — Date row is itself collapsible
                    // so a PM can hide a whole day (with all its status
                    // sub-sections + tasks) in one click. Key follows the
                    // same `${parent}::date::${date}` convention.
                    const dateKey = `${key}::date::${d}`;
                    const dateCollapsed = collapsedSubs.has(dateKey);
                    const DateChevron = dateCollapsed ? ChevronRight : ChevronDown;
                    return (
                      <React.Fragment key={`${key}-${d}`}>
                        <tr className="bg-slate-100/90 dark:bg-slate-800/50 border-t border-slate-200/70 dark:border-slate-700/60" data-testid={`report-date-${key}-${d}`}>
                          <td colSpan={14} className={`${cell} px-4 py-1`}>
                            {/* R268aaaa · Feb 2026 — Same sticky-left
                                wrap as the group header so the date +
                                per-day hours stay in view on mobile. */}
                            <div className="sticky left-4 flex items-center gap-2 text-[11px] flex-wrap max-w-[calc(100vw-32px)] sm:max-w-none">
                              <button
                                type="button"
                                onClick={() => onToggleSub(dateKey)}
                                className="inline-flex items-center gap-1 hover:text-primary shrink-0"
                                data-testid={`report-date-toggle-${key}-${d}`}
                              >
                                <DateChevron className="w-3.5 h-3.5" />
                              </button>
                              <span className="font-semibold text-foreground">{d}</span>
                              <span className="text-muted-foreground">
                                · {completedToday.length} completed · {loggedToday.length} logged only
                              </span>
                              <span className="font-mono text-foreground font-semibold"
                                    data-testid={`report-date-hours-${key}-${d}`}>
                                {dayTotal.toFixed(1)}h
                              </span>
                              {reportTotalHours > 0 && (
                                <span className="font-mono text-foreground/80 font-semibold">
                                  {((dayTotal / reportTotalHours) * 100).toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {!dateCollapsed && completedToday.length > 0 && renderStatusSection(key, "completed", completedToday, (t) => hoursOn(t, d), d, `${key}-${d}-done`, reportTotalHours)}
                        {!dateCollapsed && loggedToday.length > 0 && renderStatusSection(key, "progress", loggedToday, (t) => hoursOn(t, d), d, `${key}-${d}-log`, reportTotalHours)}
                      </React.Fragment>
                    );
                  })
                )}
                {!isCollapsed && !dateGroupEnabled && done.length > 0 && renderStatusSection(key, "completed", done, (t) => Number(t.actual_hours) || 0, null, `${key}-done`, reportTotalHours)}
                {!isCollapsed && !dateGroupEnabled && prog.length > 0 && renderStatusSection(key, "progress", prog, (t) => Number(t.actual_hours) || 0, null, `${key}-prog`, reportTotalHours)}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-muted border-t-2 border-border sticky bottom-0 z-10" data-testid="report-group-totals">
            <td colSpan={14} className={`${cell} px-3 py-2.5 bg-muted`}>
              {/* R268~23 · Feb 2026 — Footer rewritten as a labelled
                  stat-row so mobile users don't have to guess what
                  "824" or "465.8h" means. Each cell = one titled stat,
                  wrapping via flex-wrap so 5 stats stay readable at
                  360-wide viewport. */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-bold uppercase tracking-widest">Group totals</span>
                  <span className="text-[10px] text-muted-foreground">
                    · {totals.activeCount} {totals.activeCount === 1 ? "assignee" : "assignees"}
                  </span>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 flex-wrap text-[11px] font-mono">
                  <div className="flex flex-col leading-tight" data-testid="report-group-totals-est">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">Estimated</span>
                    <span className="font-semibold">{totals.totalEst.toFixed(1)}h</span>
                  </div>
                  <div className="flex flex-col leading-tight" data-testid="report-group-totals-actual">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">Actual</span>
                    <span className="font-bold">{totals.totalHours.toFixed(1)}h</span>
                  </div>
                  <div className="flex flex-col leading-tight text-emerald-700" data-testid="report-group-totals-done">
                    <span className="text-[9px] font-semibold uppercase tracking-widest">Completed</span>
                    <span className="font-semibold">{totals.doneCount} tasks · {totals.doneHours.toFixed(1)}h</span>
                  </div>
                  <div className="flex flex-col leading-tight text-blue-700" data-testid="report-group-totals-prog">
                    <span className="text-[9px] font-semibold uppercase tracking-widest">In progress</span>
                    <span className="font-semibold">{totals.progCount} tasks · {totals.progHours.toFixed(1)}h</span>
                  </div>
                  <div className="flex flex-col leading-tight" data-testid="report-group-totals-sum">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">Total hours</span>
                    <span className="font-bold">{totals.totalHours.toFixed(1)}h</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function SortHead({ label, col, sortBy, sortDir, onSortChange, width = "", align = "left", className = "" }) {
  const active = sortBy === col;
  const alignCls = align === "right" ? "text-right justify-end" : (align === "center" ? "text-center" : "text-left");
  return (
    <th className={`${width} px-1 sm:px-2 py-1 sm:py-1.5 font-semibold ${alignCls} border border-border/70 bg-muted ${className}`}>
      <button
        type="button"
        onClick={() => onSortChange(col)}
        className={`inline-flex items-center gap-1 ${active ? "text-foreground" : "hover:text-foreground"}`}
        data-testid={`report-sort-${col}`}
      >
        <span>{label}</span>
        {active ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </th>
  );
}


// R268~11 · Feb 2026 — Compact mobile sort chip. 4 chips (Task, Δ%,
// Actual, Due) bound to the shared sortBy/sortDir state. The active
// chip shows an ↑/↓ marker; tapping it flips direction, tapping any
// other chip switches column with asc.
const SORT_CHIP_OPTIONS = [
  { col: "title", label: "Title" },
  { col: "effort_variance_pct", label: "Δ%" },
  { col: "actual_hours", label: "Actual" },
  { col: "due_date", label: "Due" },
];
function MobileSortChip({ sortBy, sortDir, onChange }) {
  return (
    <div className="sm:hidden flex items-center gap-1 overflow-x-auto -mx-1 px-1"
         data-testid="report-cards-sort-chip">
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground shrink-0">Sort</span>
      {SORT_CHIP_OPTIONS.map((opt) => {
        const active = sortBy === opt.col;
        return (
          <button
            key={opt.col}
            type="button"
            onClick={() => onChange(opt.col)}
            className={`text-[10px] font-semibold px-2 py-1 border shrink-0 uppercase tracking-widest transition-colors ${
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:text-foreground"
            }`}
            data-testid={`report-cards-sort-${opt.col}`}
            aria-pressed={active}
          >
            {opt.label}
            {active && (sortDir === "asc" ? " ↑" : " ↓")}
          </button>
        );
      })}
    </div>
  );
}

// R268~11 · Feb 2026 — Snapshot the mobile cards container to a PNG,
// then either invoke the Web Share API (native share sheet on phones,
// perfect for WhatsApp forwards) or fall back to a plain download.
async function exportCardsToImage({ dateFrom, dateTo, setBusy }) {
  const node = document.querySelector('[data-testid="report-cards-flat"], [data-testid="report-cards-grouped"]');
  if (!node) {
    toast.error("Cards not visible — switch to Cards view first.");
    return;
  }
  setBusy(true);
  try {
    const html2canvas = (await import("html2canvas")).default;
    const bg = getComputedStyle(document.documentElement).getPropertyValue("--background").trim();
    const canvas = await html2canvas(node, {
      backgroundColor: bg ? `hsl(${bg})` : "#ffffff",
      scale: 2, // retina-crisp for WhatsApp
      useCORS: true,
      logging: false,
    });
    const range = dateFrom === dateTo ? dateFrom : `${dateFrom}_to_${dateTo}`;
    const filename = `daily-completion-${range}.png`;
    // Prefer Web Share API for one-tap WhatsApp forwards.
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
    if (!blob) throw new Error("Snapshot failed");
    const file = new File([blob], filename, { type: "image/png" });
    if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `Daily completion · ${range}`,
          text: `Daily completion report · ${range}`,
        });
        toast.success("Shared");
        return;
      } catch (shareErr) {
        // User cancelled or share failed → fall through to download.
        if (shareErr && shareErr.name === "AbortError") return;
      }
    }
    // Fallback: trigger download.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded PNG");
  } catch (e) {
    toast.error("Couldn't create image — try again.");
    // eslint-disable-next-line no-console
    console.error("exportCardsToImage failed", e);
  } finally {
    setBusy(false);
  }
}


// R268~10 · Feb 2026 — Mobile stacked-card view. Renders each task as
// a self-contained card: title row with status + priority icons, a
// mini pill row (Est · Actual · Δ%), timesheet notes below, and a
// small metadata footer (assignee · due · completed). Groups collapse
// to a header row so the desktop Expand/Collapse buttons keep working.
function MobileCard({ t, onOpenTask, contextHours = null, contextLabel = null }) {
  const isProgress = t.kind === "progress";
  // R268~25 · Feb 2026 — When a task sits inside a date band we prefer
  // the hours logged on THAT date over the grand-total. The parent band
  // passes `contextHours` (e.g. hoursOn(t, "2026-02-11")); the full-day
  // number still shows too so managers can compare against the plan.
  const showBoth = contextHours != null;
  // Delay chip: only meaningful when a task is DELAYED. Uses `daysDelayed`
  // vs completion (if done) or today (if still in progress).
  const delayDays = t.timing === "delayed" ? daysDelayed(t.due_date, t.completed_at) : null;
  return (
    <div
      className="border-t border-border/70 first:border-t-0 px-3 py-2 space-y-1.5 bg-background active:bg-muted/40"
      data-testid={`report-card-${t.id}`}
    >
      <div className="flex items-start gap-2">
        {isProgress ? (
          <CircleDashed className="w-4 h-4 shrink-0 text-blue-600 mt-0.5" title="In progress today" />
        ) : (
          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" title="Completed today" />
        )}
        <PriorityIcon
          priority={t.priority || "medium"}
          className="w-3.5 h-3.5 shrink-0 mt-1"
          title={`Priority: ${t.priority || "medium"}`}
        />
        <button
          type="button"
          onClick={() => onOpenTask?.(t.id)}
          className="text-[12px] font-medium text-left hover:text-primary hover:underline flex-1 leading-snug break-words"
          data-testid={`report-card-title-${t.id}`}
        >{t.title}</button>
      </div>
      <div className="flex flex-wrap items-center gap-1 pl-6">
        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-[1px] border border-border bg-muted/40 font-mono" title="Estimated hours">
          <span className="text-muted-foreground uppercase tracking-widest text-[8.5px]">est</span>
          <span className="font-semibold">{hoursFmt(t.est_hours)}</span>
        </span>
        {showBoth && (
          <span
            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-[1px] border border-blue-300 bg-blue-50 text-blue-800 font-mono"
            title={contextLabel ? `Actual hours logged on ${contextLabel}` : "Actual hours on this date"}
            data-testid={`report-card-act-today-${t.id}`}
          >
            <span className="uppercase tracking-widest text-[8.5px] opacity-80">
              {contextLabel ? contextLabel : "today"}
            </span>
            <span className="font-semibold">{hoursFmt(contextHours)}</span>
          </span>
        )}
        <span
          className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-[1px] border border-border bg-muted/40 font-mono"
          title="Actual hours (grand total across the whole task)"
          data-testid={`report-card-act-total-${t.id}`}
        >
          <span className="text-muted-foreground uppercase tracking-widest text-[8.5px]">
            {showBoth ? "total" : "act"}
          </span>
          <span className="font-semibold">{hoursFmt(t.actual_hours)}</span>
        </span>
        <span
          className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-[1px] border font-mono font-semibold"
          style={{
            borderColor: EFFORT_COLOR[t.effort_status] || "hsl(var(--border))",
            color: EFFORT_COLOR[t.effort_status] || "inherit",
          }}
          title={`Δ% (${EFFORT_LABEL[t.effort_status] || "on track"})`}
        >Δ {varFmt(t)}</span>
        {t.timing && (
          <span className="text-[9px] px-1.5 py-[1px] border font-medium uppercase tracking-widest"
                style={{ color: TIMING_COLOR[t.timing], borderColor: TIMING_COLOR[t.timing] }}
                title="Timing vs due date">
            {TIMING_LABEL[t.timing]}
            {delayDays != null && delayDays > 0 && (
              <span className="ml-1 lowercase font-semibold" data-testid={`report-card-delay-days-${t.id}`}>
                · {delayDays}d
              </span>
            )}
          </span>
        )}
      </div>
      {Array.isArray(t.notes) && t.notes.length > 0 && (
        <div
          className="pl-6 text-[11px] italic text-muted-foreground leading-snug"
          data-testid={`report-card-notes-${t.id}`}
          title={t.notes.map((n) => `${fmtNiceDate(n.date)} · ${n.hours.toFixed(1)}h — ${n.note}`).join("\n\n")}
        >
          <span className="text-foreground/80 not-italic">“</span>
          {t.notes[0].note}
          <span className="text-foreground/80 not-italic">”</span>
          {t.notes.length > 1 && (
            <span className="text-foreground/60 not-italic ml-1">+{t.notes.length - 1}</span>
          )}
        </div>
      )}
      <div className="pl-6 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9.5px] text-muted-foreground">
        {t.assignee_name && (<span title="Assignee">👤 {t.assignee_name}</span>)}
        {t.project_name && (<span title="Project">📁 {t.project_name}</span>)}
        {t.due_date && (
          <span title={`Due date: ${fmtNiceDate(t.due_date)}`} data-testid={`report-card-due-${t.id}`}>
            ⏱ Due {fmtNiceDate(t.due_date)}
            {delayDays != null && delayDays > 0 && (
              <span className="ml-1 text-rose-600 font-semibold">· {delayDays}d late</span>
            )}
          </span>
        )}
        {t.completed_at && (
          <span title={`Completed at: ${fmtNiceDate(t.completed_at)}`} data-testid={`report-card-completed-${t.id}`}>
            ✓ {fmtNiceDate(t.completed_at)}
          </span>
        )}
      </div>
    </div>
  );
}

function MobileCardsList({ groups, flatTasks, collapsed, onToggleGroup, onOpenTask,
                           groupBy, subGroupBy, collapsedSubs, onToggleSub,
                           dateFrom, dateTo }) {
  // R268~24 · Feb 2026 — Cards view now mirrors the desktop table's
  // automatic hierarchy: every parent group is auto-split into
  // COMPLETED / IN PROGRESS bands (regardless of Then-by), and — when
  // date-grouping is active (assignee_name + a date range) — each band
  // further splits into per-date rows. Then-by (project / client /
  // brand / department / status / priority) still nests under the auto
  // bands, exactly as it does in the table.
  const dateGroupEnabled = groupBy === "assignee_name" && !!dateFrom && !!dateTo;

  const enumDates = () => {
    if (!dateGroupEnabled) return [];
    const out = [];
    const a = new Date(dateFrom); const b = new Date(dateTo);
    for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  };
  const rangeDates = enumDates();
  const hoursOn = (task, dateStr) => {
    const arr = Array.isArray(task.hours_by_date) ? task.hours_by_date : [];
    for (const e of arr) { if (e.date === dateStr) return Number(e.hours) || 0; }
    return 0;
  };
  const rangeHours = (task) => {
    const arr = Array.isArray(task.hours_by_date) ? task.hours_by_date : [];
    let s = 0; for (const e of arr) s += Number(e.hours) || 0; return s;
  };

  // Optional Then-by sub-groups nested *inside* an auto Status band.
  const renderSubOrCards = (parentKey, tasks, hoursFn, contextLabel = null) => {
    const effSub = (subGroupBy && subGroupBy !== "none" && subGroupBy !== groupBy) ? subGroupBy : null;
    if (!effSub) {
      return tasks.map((t) => (
        <MobileCard
          key={`${parentKey}-${t.id}`}
          t={t}
          onOpenTask={onOpenTask}
          contextHours={hoursFn ? hoursFn(t) : null}
          contextLabel={contextLabel}
        />
      ));
    }
    const subMap = new Map();
    for (const t of tasks) {
      const sk = t[effSub] || (effSub === "priority" ? "medium" : "—");
      if (!subMap.has(sk)) subMap.set(sk, []);
      subMap.get(sk).push(t);
    }
    const subEntries = [...subMap.entries()].sort(
      (a, b) => b[1].reduce((s, t) => s + (hoursFn ? hoursFn(t) : Number(t.actual_hours) || 0), 0)
              - a[1].reduce((s, t) => s + (hoursFn ? hoursFn(t) : Number(t.actual_hours) || 0), 0)
    );
    return subEntries.map(([subKey, subTasks]) => {
      const subActual = subTasks.reduce((s, t) => s + (hoursFn ? hoursFn(t) : Number(t.actual_hours) || 0), 0);
      const subEst = subTasks.reduce((s, t) => s + (Number(t.est_hours) || 0), 0);
      const subCollapseKey = `${parentKey}::sub::${subKey}`;
      const isSubCollapsed = collapsedSubs && collapsedSubs.has(subCollapseKey);
      const testKey = `${parentKey}-${String(subKey).replace(/\s+/g, "_")}`;
      return (
        <div key={subCollapseKey} data-testid={`report-cards-subgroup-${testKey}`}>
          <button
            type="button"
            onClick={() => onToggleSub && onToggleSub(subCollapseKey)}
            className="w-full flex items-center gap-2 pl-10 pr-3 py-1.5 bg-slate-50/60 dark:bg-slate-800/20 text-left border-t border-slate-200/50 dark:border-slate-700/30"
            data-testid={`report-cards-subgroup-toggle-${testKey}`}
          >
            {isSubCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span className="text-[11px] font-semibold truncate flex-1">{subKey || "—"}</span>
            <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right w-10">
              {subTasks.length}t
            </span>
            <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right">
              {hoursFmt(subActual)} / {hoursFmt(subEst)}
            </span>
          </button>
          {!isSubCollapsed && subTasks.map((t) => (
            <MobileCard
              key={`${subCollapseKey}-${t.id}`}
              t={t}
              onOpenTask={onOpenTask}
              contextHours={hoursFn ? hoursFn(t) : null}
              contextLabel={contextLabel}
            />
          ))}
        </div>
      );
    });
  };

  // Automatic COMPLETED / IN PROGRESS status bands, always rendered
  // when a group has tasks of that kind. Colour + label mirror the
  // desktop chips ("COMPLETED" emerald, "IN PROGRESS" blue).
  const renderStatusBand = (parentKey, kind, tasks, hoursFn, labelPrefix, contextLabel = null) => {
    if (!tasks || tasks.length === 0) return null;
    const bandHours = tasks.reduce((s, t) => s + (hoursFn ? hoursFn(t) : Number(t.actual_hours) || 0), 0);
    const bandEst = tasks.reduce((s, t) => s + (Number(t.est_hours) || 0), 0);
    const collapseKey = `${parentKey}::status::${kind}`;
    const isBandCollapsed = collapsedSubs && collapsedSubs.has(collapseKey);
    const label = kind === "completed" ? "Completed" : "In progress";
    const chipClass = kind === "completed"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : "text-blue-700 bg-blue-50 border-blue-200";
    return (
      <div key={collapseKey} data-testid={`report-cards-status-${parentKey}-${kind}`}>
        <button
          type="button"
          onClick={() => onToggleSub && onToggleSub(collapseKey)}
          className="w-full flex items-center gap-2 pl-6 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800/30 text-left border-t border-slate-200/60 dark:border-slate-700/40"
          data-testid={`report-cards-status-toggle-${parentKey}-${kind}`}
        >
          {isBandCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {labelPrefix && <span className="text-[10px] text-muted-foreground font-mono">{labelPrefix}</span>}
          <span className={`text-[9px] font-bold uppercase tracking-widest border px-1.5 py-0.5 ${chipClass}`}>
            {label}
          </span>
          <span className="text-[10px] font-mono flex-1"></span>
          <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right w-10">
            {tasks.length}t
          </span>
          <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right">
            {hoursFmt(bandHours)} / {hoursFmt(bandEst)}
          </span>
        </button>
        {!isBandCollapsed && renderSubOrCards(collapseKey, tasks, hoursFn, contextLabel)}
      </div>
    );
  };

  // Split a parent group's tasks into Status → optional Then-by bands.
  const renderStatusForGroup = (parentKey, tasks) => {
    const done = tasks.filter((t) => t.kind !== "progress");
    const prog = tasks.filter((t) => t.kind === "progress");
    return (
      <>
        {renderStatusBand(parentKey, "completed", done, null, null)}
        {renderStatusBand(parentKey, "progress",  prog, null, null)}
      </>
    );
  };

  // Split a parent group by date first, then each date into status
  // bands. Only reachable when dateGroupEnabled === true.
  const renderDateBands = (parentKey, tasks) => {
    if (!rangeDates.length) return renderStatusForGroup(parentKey, tasks);
    return rangeDates.map((d) => {
      // Tasks completed ON this date OR with any hours logged on it.
      const completedToday = tasks.filter((x) => (x.completed_at || "").slice(0, 10) === d);
      const loggedToday   = tasks.filter((x) => hoursOn(x, d) > 0 && (x.completed_at || "").slice(0, 10) !== d);
      if (completedToday.length === 0 && loggedToday.length === 0) return null;
      const dayTotal = tasks.reduce((s, x) => s + hoursOn(x, d), 0);
      const dateKey = `${parentKey}::date::${d}`;
      const isDateCollapsed = collapsedSubs && collapsedSubs.has(dateKey);
      return (
        <div key={dateKey} data-testid={`report-cards-date-${parentKey}-${d}`}>
          <button
            type="button"
            onClick={() => onToggleSub && onToggleSub(dateKey)}
            className="w-full flex items-center gap-2 pl-4 pr-3 py-1.5 bg-slate-100/90 dark:bg-slate-800/50 text-left border-t border-slate-200/70 dark:border-slate-700/60"
            data-testid={`report-cards-date-toggle-${parentKey}-${d}`}
          >
            {isDateCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span className="text-[11px] font-semibold flex-1">{fmtNiceDate(d)}</span>
            <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right w-10">
              {completedToday.length + loggedToday.length}t
            </span>
            <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right">
              {hoursFmt(dayTotal)}
            </span>
          </button>
          {!isDateCollapsed && (
            <>
              {renderStatusBand(`${dateKey}`, "completed", completedToday, (t) => hoursOn(t, d), null, fmtNiceDate(d))}
              {renderStatusBand(`${dateKey}`, "progress",  loggedToday,   (t) => hoursOn(t, d), null, fmtNiceDate(d))}
            </>
          )}
        </div>
      );
    });
  };

  const renderTaskList = (parentKey, tasks) => {
    if (dateGroupEnabled) return renderDateBands(parentKey, tasks);
    return renderStatusForGroup(parentKey, tasks);
  };

  if (flatTasks) {
    // No primary group but sub / status / date bands still render via a
    // synthetic "all" parent so the hierarchy is never lost.
    return (
      <div data-testid="report-cards-flat">
        {renderTaskList("all", flatTasks)}
      </div>
    );
  }
  return (
    <div data-testid="report-cards-grouped">
      {groups.map(([grp, tasks]) => {
        const key = grp || "—";
        const isCollapsed = collapsed.has(key);
        const totalEst = tasks.reduce((s, t) => s + (Number(t.est_hours) || 0), 0);
        const totalAct = tasks.reduce(
          (s, t) => s + (dateGroupEnabled ? rangeHours(t) : (Number(t.actual_hours) || 0)),
          0,
        );
        const doneCount = tasks.filter((t) => t.kind !== "progress").length;
        const progCount = tasks.filter((t) => t.kind === "progress").length;
        return (
          <div key={key} className="border-t border-border first:border-t-0" data-testid={`report-cards-group-${key}`}>
            <button
              type="button"
              onClick={() => onToggleGroup(key)}
              className="w-full flex flex-col gap-0.5 px-3 py-2 bg-slate-200/80 dark:bg-slate-800/80 text-left"
              data-testid={`report-cards-group-toggle-${key}`}
            >
              <div className="flex items-center gap-2">
                {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <span className="text-[12px] font-semibold truncate flex-1">{key}</span>
                <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right w-10">
                  {tasks.length}t
                </span>
                <span className="text-[10px] text-muted-foreground font-mono shrink-0 tabular-nums text-right">
                  {hoursFmt(totalAct)} / {hoursFmt(totalEst)}
                </span>
              </div>
              {/* R268~24 · Feb 2026 — Micro-summary chip strip so PMs
                  can see the done/in-progress split at-a-glance without
                  expanding every card. */}
              <div className="flex items-center gap-2 pl-5 text-[10px] font-mono">
                <span className="text-emerald-700 font-semibold">
                  {doneCount} <span className="uppercase tracking-widest text-[9px]">done</span>
                </span>
                <span className="text-muted-foreground">·</span>
                <span className="text-blue-700 font-semibold">
                  {progCount} <span className="uppercase tracking-widest text-[9px]">in progress</span>
                </span>
              </div>
            </button>
            {!isCollapsed && renderTaskList(key, tasks)}
          </div>
        );
      })}
    </div>
  );
}



// Feb 2026 — Silent members panel. Fetched separately from the main
// report so it doesn't slow down first paint. Reacts to date range +
// PM filter changes and hides itself when there are zero silent people.
// R268zzz · Feb 2026 — Accepts `excludeUserIds`: any user already
// visible in the main data table (any assignee_id in scope for the
// range) is filtered out so the two sections never contradict.
function RosterChangesSection({ wsId, dateFrom, dateTo }) {
  const [data, setData] = useState({ joined: [], exited: [], on_notice: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
    api
      .get(`/workspaces/${wsId}/reports/daily-completion/roster-changes?${qs.toString()}`)
      .then((r) => { if (alive) setData(r.data || { joined: [], exited: [], on_notice: [] }); })
      .catch(() => { if (alive) setData({ joined: [], exited: [], on_notice: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [wsId, dateFrom, dateTo]);

  const jN = data.joined?.length || 0;
  const eN = data.exited?.length || 0;
  const nN = data.on_notice?.length || 0;
  if (!loading && jN === 0 && eN === 0 && nN === 0) return null;

  return (
    <div className="border border-border" data-testid="roster-changes-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200 hover:bg-blue-100 text-left"
        data-testid="roster-changes-toggle"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="text-sm font-semibold text-blue-900">Roster changes this period</span>
        <span className="text-xs text-blue-700 font-mono">
          {loading ? "loading…" : (
            <>
              {jN > 0 && <span data-testid="roster-changes-joined-count" className="mr-2">{jN} joined</span>}
              {eN > 0 && <span data-testid="roster-changes-exited-count" className="mr-2">{eN} exited</span>}
              {nN > 0 && <span data-testid="roster-changes-notice-count">{nN} on notice</span>}
              {jN + eN + nN === 0 && "no changes"}
            </>
          )}
        </span>
      </button>
      {expanded && (
        <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <RosterList label="Joined" tone="emerald" items={data.joined} showDate />
          <RosterList label="Exited" tone="rose" items={data.exited} showDate />
          <RosterList label="On notice" tone="amber" items={data.on_notice} />
        </div>
      )}
    </div>
  );
}

function RosterList({ label, tone, items, showDate }) {
  const toneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  }[tone] || "bg-secondary border-border text-foreground";
  return (
    <div className={`border ${toneCls} p-2`} data-testid={`roster-changes-${label.toLowerCase()}`}>
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-1">{label}</div>
      {items.length === 0 ? (
        <div className="text-[11px] opacity-70">None</div>
      ) : (
        <ul className="space-y-1">
          {items.map((r, i) => (
            <li key={`${r.employee_id}-${i}`} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
              <span className="font-medium truncate">{r.name}</span>
              {showDate && r.on && <span className="text-[10px] font-mono opacity-70">{r.on}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function SilentMembersSection({ wsId, dateFrom, dateTo, pmFilter, excludeUserIds }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    let alive = true;
    setLoading(true);
    const qs = new URLSearchParams({ date: dateFrom, date_to: dateTo });
    if (pmFilter && pmFilter.size) qs.set("pm_ids", [...pmFilter].join(","));
    api.get(`/workspaces/${wsId}/reports/daily-completion/silent-members?${qs.toString()}`)
      .then((r) => { if (alive) setRows(r.data?.members || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [wsId, dateFrom, dateTo, pmFilter]);

  const visibleRows = useMemo(() => {
    if (!excludeUserIds || excludeUserIds.size === 0) return rows;
    return rows.filter((m) => !excludeUserIds.has(m.user_id));
  }, [rows, excludeUserIds]);

  if (!loading && visibleRows.length === 0) return null;

  return (
    <div className="border border-border" data-testid="silent-members-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 hover:bg-amber-100 text-left"
        data-testid="silent-members-toggle"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="text-sm font-semibold text-amber-900">
          Silent members
        </span>
        <span className="text-xs text-amber-700 font-mono">
          {loading ? "loading…" : `${visibleRows.length} · no completed tasks in range`}
        </span>
      </button>
      {expanded && (
        <div className="p-2 flex flex-wrap gap-2" data-testid="silent-members-grid">
          {visibleRows.map((m) => (
            <UserTimesheetHover
              key={m.user_id}
              wsId={wsId}
              userId={m.user_id}
              dateFrom={dateFrom}
              dateTo={dateTo}
            >
              <div className="border border-border bg-background px-2.5 py-1.5 flex items-center gap-2 cursor-help hover:border-primary min-w-[220px]"
                   data-testid={`silent-member-${m.user_id}`}>
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  {m.avatar_data_url ? (
                    <img src={m.avatar_data_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                      {(m.name || "?").slice(0, 2)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate leading-tight">{m.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate leading-tight">
                    {m.email || "—"}
                  </div>
                  {/* R268~12 · Feb 2026 — Approved-leave chip so a
                      manager instantly sees WHY someone had no effort
                      on this window. Multiple leaves stack. */}
                  {Array.isArray(m.leaves) && m.leaves.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5" data-testid={`silent-leaves-${m.user_id}`}>
                      {m.leaves.map((lv, idx) => (
                        // R268~16 · Feb 2026 — Tap-to-open popover with
                        // leave note + approver so managers can verify
                        // without leaving DCR for HR.
                        <Popover key={idx}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center text-[9px] px-1 py-[1px] bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 uppercase tracking-widest font-semibold hover:bg-amber-500/25 focus:outline-none focus:ring-1 focus:ring-amber-500"
                              data-testid={`silent-leave-chip-${m.user_id}-${idx}`}
                              title={`${lv.leave_type} · ${lv.from_date} → ${lv.to_date}`}
                            >
                              {lv.from_date === lv.to_date
                                ? `On leave · ${lv.from_date.slice(5)}`
                                : `On leave · ${lv.from_date.slice(5)}→${lv.to_date.slice(5)}`}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent side="top" align="start" className="w-64 p-3 text-xs space-y-1.5 rounded-none" data-testid={`silent-leave-popover-${m.user_id}-${idx}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-300">
                                {lv.leave_type} leave
                              </span>
                              <span className="text-[10px] text-muted-foreground font-mono">
                                {lv.from_date}{lv.from_date !== lv.to_date && ` → ${lv.to_date}`}
                              </span>
                            </div>
                            {lv.note ? (
                              <div className="italic text-foreground/90 whitespace-pre-wrap break-words">
                                “{lv.note}”
                              </div>
                            ) : (
                              <div className="italic text-muted-foreground/70">No note added.</div>
                            )}
                            <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
                              {lv.approver_name
                                ? <>Approved by <span className="text-foreground font-medium">{lv.approver_name}</span></>
                                : <>Approver unavailable</>}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-[9.5px] text-right font-mono shrink-0">
                  <div className={`font-semibold ${m.logged_hours > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {m.logged_hours}h
                  </div>
                  <div className="text-muted-foreground">
                    {m.in_progress_tasks + m.open_tasks} pending
                  </div>
                </div>
              </div>
            </UserTimesheetHover>
          ))}
        </div>
      )}
    </div>
  );
}
